import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Download,
  FileBarChart2,
  Plus,
  Send,
  Stamp,
  Trash2,
} from "lucide-react";

import { ApiError, formatDate } from "../../lib/api";
import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  PromptDialog,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import {
  ESTADO_LABELS,
  anularReajuste,
  aplicarReajuste,
  descargarInformePdf,
  enviarSiss,
  fetchReajustes,
  registrarVistoBueno,
  type EstadoReajuste,
  type Reajuste,
} from "./api";
import { CrearReajusteModal } from "./CrearReajusteModal";

const ESTADOS: EstadoReajuste[] = [
  "Borrador",
  "EnviadoSISS",
  "VistoBueno",
  "Aplicado",
  "Anulado",
];

export function ReajustesListPage(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [estado, setEstado] = useState<EstadoReajuste | "">("");
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [aplicando, setAplicando] = useState<Reajuste | null>(null);

  const listQ = useQuery({
    queryKey: ["reajustes", estado],
    queryFn: () => fetchReajustes({ estado: estado || null }),
  });

  const enviarMut = useMutation({
    mutationFn: (id: number) => enviarSiss(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reajustes"] });
      toast.success("Marcado como enviado a SISS");
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error";
      toast.error(msg);
    },
  });

  const vbMut = useMutation({
    mutationFn: ({ id, numero }: { id: number; numero: string }) =>
      registrarVistoBueno(id, numero),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reajustes"] });
      toast.success("Visto bueno registrado");
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error";
      toast.error(msg);
    },
  });

  const aplicarMut = useMutation({
    mutationFn: (id: number) => aplicarReajuste(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reajustes"] });
      qc.invalidateQueries({ queryKey: ["tarifas"] });
      toast.success("Reajuste aplicado — nuevas vigencias creadas");
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error al aplicar";
      toast.error(msg);
    },
  });

  const anularMut = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) =>
      anularReajuste(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reajustes"] });
      toast.success("Reajuste anulado");
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error";
      toast.error(msg);
    },
  });

  const items = listQ.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Reajuste tarifario"
        icon={FileBarChart2}
        subtitle={
          <>
            Reajuste por IPC según Manual SISS Facturación cap. 7. Flujo:{" "}
            <strong>Borrador → Enviado a SISS → Visto bueno → Aplicado</strong>.
            El PDF del informe (cuadros 1+2) se genera al crear y se entrega a
            la oficina SISS regional. Sólo aplicable desde el día 15 del mes.
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setCrearAbierto(true)}>
            Nuevo reajuste
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Field label="Estado">
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoReajuste | "")}
            className="rounded-md border border-neutral-450 px-2 py-1.5 text-sm shadow-sm"
          >
            <option value="">Todos</option>
            {ESTADOS.map((s) => (
              <option key={s} value={s}>
                {ESTADO_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.isPending && <Cargando />}
      {listQ.isError && (
        <ErrorCarga
          error={listQ.error}
          mensaje="Error al cargar reajustes."
          onReintentar={() => void listQ.refetch()}
        />
      )}

      {items.length === 0 && listQ.data && (
        <p className="text-sm text-neutral-500">
          Sin reajustes registrados. Crea uno cuando el IPC acumule ≥5% o pase
          un año desde el último reajuste.
        </p>
      )}

      {items.length > 0 && (
        <TableWrap label="Reajustes tarifarios">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Vigencia</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Variación</th>
                <th scope="col" className="px-3 py-2 text-left">IPC₀ → IPC actual</th>
                <th scope="col" className="px-3 py-2 text-left">Localidades</th>
                <th scope="col" className="px-3 py-2 text-left">VB SISS</th>
                <th scope="col" className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((r) => (
                <ReajusteRow
                  key={r.id}
                  reajuste={r}
                  onEnviar={() => enviarMut.mutate(r.id)}
                  onVistoBueno={(numero) =>
                    vbMut.mutate({ id: r.id, numero })
                  }
                  onAplicar={() => setAplicando(r)}
                  onAnular={(motivo) =>
                    anularMut.mutate({ id: r.id, motivo })
                  }
                  onDescargarPdf={async () => {
                    try {
                      await descargarInformePdf(r.id);
                      toast.success("PDF descargado");
                    } catch {
                      toast.error("No se pudo descargar el PDF");
                    }
                  }}
                />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      <ConfirmDialog
        open={aplicando !== null}
        title="Aplicar reajuste"
        tone="default"
        description={
          aplicando && (
            <>
              Se crearán vigencias nuevas de cargos fijos y tramos desde el{" "}
              <strong>{formatDate(aplicando.fecha_vigencia)}</strong> (variación{" "}
              {Number(aplicando.variacion_pct).toFixed(2)}%). Las vigencias
              anteriores quedan cerradas.
            </>
          )
        }
        confirmLabel="Aplicar reajuste"
        loading={aplicarMut.isPending}
        onCancel={() => setAplicando(null)}
        onConfirm={() => {
          if (!aplicando) return;
          aplicarMut.mutate(aplicando.id, { onSettled: () => setAplicando(null) });
        }}
      />

      {crearAbierto && (
        <CrearReajusteModal
          onClose={() => setCrearAbierto(false)}
          onCreated={() => {
            setCrearAbierto(false);
            qc.invalidateQueries({ queryKey: ["reajustes"] });
            toast.success("Reajuste creado en Borrador");
          }}
        />
      )}
    </div>
  );
}

function ReajusteRow({
  reajuste,
  onEnviar,
  onVistoBueno,
  onAplicar,
  onAnular,
  onDescargarPdf,
}: {
  reajuste: Reajuste;
  onEnviar: () => void;
  onVistoBueno: (numero: string) => void;
  onAplicar: () => void;
  onAnular: (motivo: string) => void;
  onDescargarPdf: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const variacion = Number(reajuste.variacion_pct);
  const sube = variacion > 0;

  return (
    <>
      <tr className={open ? "bg-neutral-50" : "hover:bg-neutral-50"}>
        <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">
          {formatDate(reajuste.fecha_vigencia)}
        </td>
        <td className="px-3 py-2">
          <EstadoBadge estado={reajuste.estado} />
        </td>
        <td className="px-3 py-2 text-right tabular-nums">
          {/* Variación: sube rojo (rose) / baja verde (emerald) — preservado. */}
          <span className={sube ? "text-rose-700" : "text-emerald-700"}>
            {sube ? "+" : ""}
            {variacion.toFixed(2)}%
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-neutral-600">
          {reajuste.ipc_0_valor} → {reajuste.ipc_actual_valor}
          <span className="text-neutral-500">
            {" "}
            (IN {Number(reajuste.factor_in).toFixed(4)})
          </span>
        </td>
        <td className="px-3 py-2 text-neutral-600 max-w-md truncate" title={reajuste.localidades_afectadas}>
          {reajuste.localidades_afectadas}
        </td>
        <td className="px-3 py-2 text-xs text-neutral-600">
          {reajuste.numero_visto_bueno || "—"}
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              icon={Download}
              onClick={onDescargarPdf}
              aria-label="Descargar PDF"
              title="Descargar PDF"
            />
            <Button variant="link" size="sm" onClick={() => setOpen((v) => !v)}>
              {open ? "Ocultar" : "Detalle"}
            </Button>
          </div>
        </td>
      </tr>
      {open && (
        <tr className="bg-neutral-50/60">
          <td colSpan={7} className="px-4 py-3 text-sm">
            <DetalleSection
              reajuste={reajuste}
              onEnviar={onEnviar}
              onVistoBueno={onVistoBueno}
              onAplicar={onAplicar}
              onAnular={onAnular}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function DetalleSection({
  reajuste,
  onEnviar,
  onVistoBueno,
  onAplicar,
  onAnular,
}: {
  reajuste: Reajuste;
  onEnviar: () => void;
  onVistoBueno: (numero: string) => void;
  onAplicar: () => void;
  onAnular: (motivo: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-3">
      <TableWrap className="rounded">
        <table className="min-w-full divide-y divide-neutral-200 text-xs">
          <thead className="bg-white text-neutral-500">
            <tr>
              <th scope="col" className="px-2 py-1 text-left">Concepto</th>
              <th scope="col" className="px-2 py-1 text-left">Detalle</th>
              <th scope="col" className="px-2 py-1 text-right">Anterior</th>
              <th scope="col" className="px-2 py-1 text-right">Nuevo</th>
              <th scope="col" className="px-2 py-1"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100 bg-white">
            {reajuste.detalles.map((d) => (
              <tr key={d.id}>
                <td className="px-2 py-1">{d.concepto}</td>
                <td className="px-2 py-1 text-neutral-600">
                  {d.subconcepto || "—"}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  ${Number(d.valor_anterior).toLocaleString("es-CL")}
                </td>
                <td className="px-2 py-1 text-right tabular-nums font-medium">
                  ${Number(d.valor_nuevo).toLocaleString("es-CL")}
                </td>
                <td className="px-2 py-1">
                  {d.valor_nuevo_editado && (
                    <span
                      title="Editado manualmente"
                      className="text-amber-700 text-xs"
                    >
                      *
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>

      <ReajusteActions
        reajuste={reajuste}
        onEnviar={onEnviar}
        onVistoBueno={onVistoBueno}
        onAplicar={onAplicar}
        onAnular={onAnular}
      />
    </div>
  );
}

function ReajusteActions({
  reajuste,
  onEnviar,
  onVistoBueno,
  onAplicar,
  onAnular,
}: {
  reajuste: Reajuste;
  onEnviar: () => void;
  onVistoBueno: (numero: string) => void;
  onAplicar: () => void;
  onAnular: (motivo: string) => void;
}): JSX.Element {
  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [pedirMotivo, setPedirMotivo] = useState(false);
  const [pedirVistoBueno, setPedirVistoBueno] = useState(false);
  const botonAnular = (
    <Button
      variant="secondary"
      size="sm"
      icon={Trash2}
      className="text-rose-700 ring-rose-300 hover:bg-rose-50"
      onClick={() => setPedirMotivo(true)}
    >
      Anular
    </Button>
  );
  return (
    <div className="flex flex-wrap gap-2">
      {reajuste.estado === "Borrador" && (
        <>
          <Button size="sm" icon={Send} onClick={() => setConfirmarEnvio(true)}>
            Marcar como enviado a SISS
          </Button>
          {botonAnular}
          <ConfirmDialog
            open={confirmarEnvio}
            title="Marcar como enviado a SISS"
            tone="default"
            description={
              <>
                El reajuste con vigencia{" "}
                <strong>{formatDate(reajuste.fecha_vigencia)}</strong> pasa a
                &quot;Enviado a SISS&quot; y queda a la espera del visto bueno.
              </>
            }
            confirmLabel="Marcar como enviado"
            onCancel={() => setConfirmarEnvio(false)}
            onConfirm={() => {
              setConfirmarEnvio(false);
              onEnviar();
            }}
          />
        </>
      )}

      {reajuste.estado === "EnviadoSISS" && (
        <>
          <Button
            size="sm"
            icon={Stamp}
            className="bg-emerald-700 hover:bg-emerald-800 focus-visible:ring-emerald-500"
            onClick={() => setPedirVistoBueno(true)}
          >
            Registrar visto bueno
          </Button>
          {botonAnular}
        </>
      )}

      <PromptDialog
        open={pedirMotivo}
        title="Anular reajuste"
        tone="danger"
        description={
          <>
            El reajuste con vigencia{" "}
            <strong>{formatDate(reajuste.fecha_vigencia)}</strong> queda anulado
            y no se puede aplicar. El motivo queda en la auditoría.
          </>
        }
        label="Motivo de la anulación"
        minLength={3}
        maxLength={255}
        confirmLabel="Anular reajuste"
        onCancel={() => setPedirMotivo(false)}
        onConfirm={(motivo) => {
          setPedirMotivo(false);
          onAnular(motivo);
        }}
      />
      <PromptDialog
        open={pedirVistoBueno}
        title="Registrar visto bueno SISS"
        description="Con el visto bueno el reajuste queda listo para aplicarse."
        label="N° de visto bueno SISS"
        placeholder="VB-2026-001"
        maxLength={50}
        confirmLabel="Registrar visto bueno"
        onCancel={() => setPedirVistoBueno(false)}
        onConfirm={(numero) => {
          setPedirVistoBueno(false);
          onVistoBueno(numero);
        }}
      />

      {reajuste.estado === "VistoBueno" && (
        <Button
          size="sm"
          icon={CheckCircle2}
          className="bg-emerald-700 hover:bg-emerald-800 focus-visible:ring-emerald-500"
          onClick={onAplicar}
        >
          Aplicar (crea nueva vigencia)
        </Button>
      )}

      {reajuste.estado === "Aplicado" && (
        <p className="text-xs text-emerald-700">
          ✓ Aplicado — vigencia activa desde {formatDate(reajuste.fecha_vigencia)}
        </p>
      )}

      {reajuste.estado === "Anulado" && reajuste.motivo_anulacion && (
        <p className="text-xs text-neutral-600">
          Anulado: {reajuste.motivo_anulacion}
        </p>
      )}
    </div>
  );
}

// EnviadoSISS era `blue` (off-palette): pasa a `brand`. Aplicado/VistoBueno
// mantienen `emerald` (éxito); Borrador/Anulado quedan en `slate` (neutro).
const ESTADO_TONE: Record<EstadoReajuste, BadgeTone> = {
  Borrador: "slate",
  EnviadoSISS: "brand",
  VistoBueno: "emerald",
  Aplicado: "emerald",
  Anulado: "slate",
};

function EstadoBadge({ estado }: { estado: EstadoReajuste }): JSX.Element {
  return <Badge tone={ESTADO_TONE[estado]}>{ESTADO_LABELS[estado]}</Badge>;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
